import { Manager, type ManagerOptions, type Socket } from "socket.io-client";
import {
  ACTIVE_ROLE_HEADER,
  buildActiveRoleAuth,
  getActiveRoleHeaderValue,
} from "@/api/active-role-header";
import { EnvVariables } from "@/env-variables";
import LocalStorage from "@/storages/LocalStorage";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { applyAuthPayload } from "./socket-auth";

export type ConsultationNamespace = "/consultation-note" | "/consultation-lock";
export const CONSULTATION_NOTE_NAMESPACE: ConsultationNamespace = "/consultation-note";
export const CONSULTATION_LOCK_NAMESPACE: ConsultationNamespace = "/consultation-lock";

const DEFAULT_MANAGER_OPTIONS: Partial<ManagerOptions> = {
  transports: ["websocket"],
  reconnectionAttempts: 3,
  reconnectionDelay: 1000,
  timeout: 20000,
  autoConnect: false,
  withCredentials: false,
};

// Read at call time, never captured: a role switch must reach the next handshake.
const selectedRole = () => useAdditionalInfo.getState().role;

const buildAuthPayload = () => {
  const accessToken = LocalStorage.getAccessToken();
  return buildActiveRoleAuth(accessToken ? { token: accessToken } : {}, selectedRole());
};

class ConsultationSocketManager {
  private manager: Manager | null = null;
  private namespaceSockets: Map<ConsultationNamespace, Socket> = new Map();

  private ensureManager(): Manager {
    if (this.manager) {
      return this.manager;
    }

    if (!EnvVariables.websocketUrl) {
      throw new Error("WebSocket URL not configured.");
    }

    const roleName = getActiveRoleHeaderValue(selectedRole());

    this.manager = new Manager(EnvVariables.websocketUrl, {
      ...DEFAULT_MANAGER_OPTIONS,
      ...(roleName ? { extraHeaders: { [ACTIVE_ROLE_HEADER]: roleName } } : {}),
    });

    return this.manager;
  }

  public getSocket(namespace: ConsultationNamespace): Socket {
    const existing = this.namespaceSockets.get(namespace);
    if (existing) {
      return existing;
    }

    const manager = this.ensureManager();
    const socket = manager.socket(namespace, {
      auth: buildAuthPayload(),
    });

    this.namespaceSockets.set(namespace, socket);
    return socket;
  }

  /**
   * Rebuild the handshake payload for every cached namespace socket.
   *
   * Called after a silent token refresh and after the active role changes. Live connections
   * are deliberately left alone: each namespace picks the new payload up on its next
   * handshake, so a role switch never drops a consultation mid-edit.
   *
   * @returns How many namespace sockets were updated
   */
  public refreshAuthPayload(): number {
    return applyAuthPayload(this.namespaceSockets.values(), buildAuthPayload());
  }

  /**
   * Dispose all namespace sockets and the shared manager.
   * Typically used only in rare teardown scenarios.
   */
  public reset(): void {
    this.namespaceSockets.forEach((socket) => {
      socket.disconnect();
    });
    this.namespaceSockets.clear();
    this.manager = null;
  }
}

const consultationSocketManager = new ConsultationSocketManager();
export default consultationSocketManager;
